import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Player } from '../../types';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useAppContext } from '../../context/AppContext';
import {
    createInviteToken,
    generateInviteLink,
    searchUsersByPseudo,
    sendInviteNotification
} from '../../utils/inviteHelpers';
import { getLevelTitleFromRating } from '../../utils/levels';

interface InvitePlayerModalProps {
    isOpen: boolean;
    onClose: () => void;
    type: 'group' | 'tournament';
    groupId: string;
    groupName: string;
    tournamentId?: string;
    tournamentName?: string;
    existingMemberIds?: string[];
    privacyMode?: 'open' | 'request' | 'invite' | 'locked' | string;
}

export const InvitePlayerModal: React.FC<InvitePlayerModalProps> = ({
    isOpen,
    onClose,
    type,
    groupId,
    groupName,
    tournamentId,
    tournamentName,
    existingMemberIds = [],
    privacyMode = 'open'
}) => {
    const { t } = useTranslation();
    const { user, userData } = useAuth();
    const { showToast } = useAppContext();

    // Tab state
    const [activeTab, setActiveTab] = useState<'search' | 'link'>('search');

    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<Player[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [invitedIds, setInvitedIds] = useState<Set<string>>(new Set());

    // Link state
    const [inviteLink, setInviteLink] = useState('');
    const [isGeneratingLink, setIsGeneratingLink] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);

    // Reset on close
    useEffect(() => {
        if (!isOpen) {
            setSearchQuery('');
            setSearchResults([]);
            setInviteLink('');
            setLinkCopied(false);
            setInvitedIds(new Set());
        }
    }, [isOpen]);

    // Search users
    useEffect(() => {
        const timer = setTimeout(async () => {
            if (searchQuery.length >= 2) {
                setIsSearching(true);
                const results = await searchUsersByPseudo(
                    searchQuery,
                    [...existingMemberIds, user?.uid || ''],
                    10
                );
                setSearchResults(results);
                setIsSearching(false);
            } else {
                setSearchResults([]);
            }
        }, 300); // Debounce

        return () => clearTimeout(timer);
    }, [searchQuery, existingMemberIds, user?.uid]);

    const handleInvitePlayer = async (player: Player) => {
        if (!user || !userData) return;

        const success = await sendInviteNotification(
            player.id,
            user.uid,
            userData.pseudo || userData.displayName || t('profile.playerFallback', 'Joueur'),
            type,
            groupId,
            groupName,
            tournamentId,
            tournamentName
        );

        if (success) {
            setInvitedIds(prev => new Set(prev).add(player.id));
            showToast(t('invite.invitationSent', { name: player.pseudo || player.name }), 'success');
        } else {
            showToast(t('invite.sendError', "Erreur lors de l'envoi de l'invitation."), 'error');
        }
    };

    const handleGenerateLink = async () => {
        if (!user || !userData) return;

        setIsGeneratingLink(true);
        try {
            const isInviteOnly = privacyMode === 'invite';
            
            const token = await createInviteToken(
                type,
                groupId,
                groupName,
                user.uid,
                userData.pseudo || userData.displayName || t('profile.playerFallback', 'Joueur'),
                tournamentId,
                tournamentName,
                isInviteOnly ? 7 : 365, // 7 days expiry for invite-only, else 1 year
                isInviteOnly ? 1 : -1  // Single use (disposable link) for invite-only, else unlimited
            );
            setInviteLink(generateInviteLink(token.id));
        } catch (error) {
            console.error('Error generating invite link:', error);
            showToast(t('invite.genError', "Erreur lors de la génération du lien."), 'error');
        } finally {
            setIsGeneratingLink(false);
        }
    };

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(inviteLink);
            setLinkCopied(true);
            showToast(t('invite.copied', "Lien copié !"), 'success');
            setTimeout(() => setLinkCopied(false), 2000);
        } catch {
            showToast(t('invite.copyError', "Impossible de copier le lien."), 'error');
        }
    };

    const handleShareLink = async () => {
        const shareData = {
            title: type === 'tournament'
                ? t('invite.shareTitleTournament', { name: tournamentName })
                : t('invite.shareTitleGroup', { name: groupName }),
            text: type === 'tournament'
                ? t('invite.shareTextTournament', { name: tournamentName })
                : t('invite.shareTextGroup', { name: groupName }),
            url: inviteLink
        };

        if (navigator.share) {
            try {
                await navigator.share(shareData);
            } catch (err) {
                // User cancelled share
            }
        } else {
            handleCopyLink();
        }
    };

    const targetLabel = type === 'tournament' ? t('invite.targetTournament', { name: tournamentName }) : t('invite.targetGroup', { name: groupName });

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={t('groups.invitePlayer', 'Inviter un joueur')}>
            <div className="space-y-4">
                {/* Tabs */}
                <div className="flex rounded-lg bg-surface-2 p-1">
                    <button
                        onClick={() => setActiveTab('search')}
                        className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${activeTab === 'search'
                            ? 'bg-primary text-white'
                            : 'text-text-secondary hover:text-text-primary'
                            }`}
                    >
                        <span className="material-symbols-outlined text-sm mr-1 align-middle">search</span>
                        {t('invite.searchTab', 'Rechercher')}
                    </button>
                    <button
                        onClick={() => setActiveTab('link')}
                        className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${activeTab === 'link'
                            ? 'bg-primary text-white'
                            : 'text-text-secondary hover:text-text-primary'
                            }`}
                    >
                        <span className="material-symbols-outlined text-sm mr-1 align-middle">link</span>
                        {t('invite.linkTab', "Lien d'invitation")}
                    </button>
                </div>

                {/* Search Tab */}
                {activeTab === 'search' && (
                    <div className="space-y-4">
                        <p className="text-sm text-text-secondary">
                            {t('invite.searchDesc', { target: targetLabel })}
                        </p>

                        {/* Search Input */}
                        <div className="relative">
                            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary">
                                search
                            </span>
                            <input
                                type="text"
                                placeholder={t('search.pseudo', 'Rechercher un pseudo...')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 bg-surface-2 rounded-lg border border-white/10 focus:border-primary focus:outline-none"
                            />
                            {isSearching && (
                                <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary animate-spin">
                                    progress_activity
                                </span>
                            )}
                        </div>

                        {/* Results */}
                        <div className="max-h-60 overflow-y-auto space-y-2">
                            {searchResults.length === 0 && searchQuery.length >= 2 && !isSearching && (
                                <div className="text-center py-4 text-text-secondary">
                                    {t('invite.noPlayerFound', 'Aucun joueur trouvé')}
                                </div>
                            )}
                            {searchResults.map(player => (
                                <div
                                    key={player.id}
                                    className="flex items-center justify-between p-3 bg-surface-2 rounded-lg"
                                >
                                    <div className="flex items-center gap-3">
                                        {(() => {
                                            const avatarSrc = player.avatar || player.photoURL;
                                            if (avatarSrc && (avatarSrc.startsWith('http') || avatarSrc.startsWith('/') || avatarSrc.startsWith('data:'))) {
                                                return (
                                                    <img
                                                        src={avatarSrc}
                                                        alt={player.pseudo || player.name}
                                                        className="w-10 h-10 rounded-full object-cover bg-surface-3"
                                                    />
                                                );
                                            } else {
                                                return (
                                                    <div className="w-10 h-10 rounded-full bg-surface-3 flex items-center justify-center text-lg border border-white/10">
                                                        {player.avatar && player.avatar.length <= 4 ? (
                                                            <span>{player.avatar}</span>
                                                        ) : (
                                                            <span className="font-bold text-primary">
                                                                {player.pseudo?.charAt(0) || player.name?.charAt(0) || '?'}
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            }
                                        })()}
                                        <div>
                                            <div className="font-medium">{player.pseudo || player.name}</div>
                                            <div className="text-xs text-text-secondary">
                                                {t(getLevelTitleFromRating(player.rating || 1500))}
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleInvitePlayer(player)}
                                        disabled={invitedIds.has(player.id)}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${invitedIds.has(player.id)
                                            ? 'bg-success/20 text-success cursor-default'
                                            : 'bg-primary text-white hover:bg-primary-hover'
                                            }`}
                                    >
                                        {invitedIds.has(player.id) ? (
                                            <>
                                                <span className="material-symbols-outlined text-sm mr-1 align-middle">check</span>
                                                {t('invite.invited', 'Invité')}
                                            </>
                                        ) : (
                                            t('invite.inviteBtn', 'Inviter')
                                        )}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Link Tab */}
                {activeTab === 'link' && (
                    <div className="space-y-4">
                        <p className="text-sm text-text-secondary overflow-hidden text-ellipsis whitespace-nowrap">
                            {privacyMode === 'invite' 
                                ? "Ce lien est jetable : il expire dans 7 jours et ne peut être utilisé qu'une seule fois." 
                                : "Ce lien est permanent : il permet à n'importe qui de retrouver le groupe/tournoi pour le rejoindre."}
                        </p>

                        {!inviteLink ? (
                            <button
                                onClick={handleGenerateLink}
                                disabled={isGeneratingLink}
                                className="w-full py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-hover disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                            >
                                {isGeneratingLink ? (
                                    <>
                                        <span className="material-symbols-outlined animate-spin">progress_activity</span>
                                        {t('invite.generating', 'Génération...')}
                                    </>
                                ) : (
                                    <>
                                        <span className="material-symbols-outlined">add_link</span>
                                        {t('invite.generateLink', 'Générer un lien')}
                                    </>
                                )}
                            </button>
                        ) : (
                            <div className="space-y-3">
                                {/* Link Display */}
                                <div className="p-3 bg-surface-2 rounded-lg border border-white/10 break-all text-sm font-mono">
                                    {inviteLink}
                                </div>

                                {/* Action Buttons */}
                                <div className="flex gap-2">
                                    <button
                                        onClick={handleCopyLink}
                                        className={`flex-1 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${linkCopied
                                            ? 'bg-success text-white'
                                            : 'bg-surface-2 text-text-primary hover:bg-surface-3'
                                            }`}
                                    >
                                        <span className="material-symbols-outlined">
                                            {linkCopied ? 'check' : 'content_copy'}
                                        </span>
                                        {linkCopied ? t('invite.copied', 'Copié !') : t('invite.copy', 'Copier')}
                                    </button>
                                    <button
                                        onClick={handleShareLink}
                                        className="flex-1 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-hover transition-all flex items-center justify-center gap-2"
                                    >
                                        <span className="material-symbols-outlined">share</span>
                                        {t('invite.share', 'Partager')}
                                    </button>
                                </div>

                                {/* Generate New */}
                                <button
                                    onClick={handleGenerateLink}
                                    className="w-full text-sm text-text-secondary hover:text-text-primary transition-colors"
                                >
                                    {t('invite.generateNew', 'Générer un nouveau lien')}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
};
